import { getRootEnv } from '@bringg-frontend/bringg-web-infra';
import { action, computed, observable, makeObservable } from 'mobx';
import each from 'lodash/each';
import every from 'lodash/every';
import isEmpty from 'lodash/isEmpty';
import { v4 as uuidv4 } from 'uuid';
import isNil from 'lodash/isNil';
import {
	ANALYTICS_WEBHOOK_CONDITION_KEY,
	ANALYTICS_WEBHOOK_UNASSIGNED_CONDITION_VALUE,
	WebhookAuthenticationConfigurationDto,
	WebhookKey
} from '@bringg/types';
import { isValidPublicUrl, normalizeUrl } from '@bringg-frontend/utils';

import WebhooksAppStore from '../webhooks-app-store';
import { Condition, WebhookData, WebhookAMC, WebhookFieldsTree } from '../webhooks.consts';
import {
	formatTreeToServer,
	formatTreeFromServer,
	formatTreeToSelectedFields
} from './server-webhook-fields-adapter/server-webhook-fields-adapter';

interface WebhookAuthenticationConfiguration {
	name: WebhookAuthenticationConfigurationDto['name'];
}

interface IWebhook {
	setConditionKey: (conditionKey: string) => void;
	setConditionValue: (conditionValue: string) => void;
	setWebhookFieldsTree: (newTree: WebhookFieldsTree) => void;
	applyUnassignedConditionIfNeeded: () => void;
	setWebhookAuthenticationConfigurationId: (id: number) => void;
	setWebhookAuthenticationConfiguration: (param: WebhookAuthenticationConfiguration) => void;
	setHeaders: (headers: Record<string, string>) => void;
	setBaseBody: (baseBody: Record<string, string>) => void;
}

class Webhook implements IWebhook {
	urlsData: Map<string, string> = new Map();

	key: string;

	isFlexible = false;

	newStructure: boolean; // Continue to support old flexible structure

	conditionKey: string;

	conditionValue: string;

	private _webhookFieldsTree: WebhookFieldsTree;

	uuid: string;

	webhooksAppStore: WebhooksAppStore;

	webhookAuthenticationConfigurationId: number | undefined;

	webhookAuthenticationConfiguration: WebhookAuthenticationConfiguration | undefined;

	headersData: Record<string, string>;

	baseBodyData: Record<string, string>;

	constructor(webhookData: WebhookData, webhooksAppStore: WebhooksAppStore) {
		makeObservable<Webhook, '_webhookFieldsTree'>(this, {
			conditionKey: observable,
			conditionValue: observable,
			_webhookFieldsTree: observable,
			urlsData: observable.shallow,
			key: observable,
			isFlexible: observable,
			newStructure: observable,
			setKey: action,
			addUrl: action,
			deleteUrl: action,
			updateUrl: action,
			setIsFlexible: action,
			updateNodeValues: action,
			resetWebhookFieldsTree: action,
			urls: computed,
			webhookFieldsTree: computed,
			isValid: computed,
			setConditionKey: action,
			setConditionValue: action,
			applyUnassignedConditionIfNeeded: action,
			setWebhookFieldsTree: action,
			webhookAuthenticationConfigurationId: observable,
			setWebhookAuthenticationConfigurationId: action,
			webhookAuthenticationConfiguration: observable,
			setWebhookAuthenticationConfiguration: action,
			setHeaders: action,
			headersData: observable,
			setBaseBody: action,
			baseBodyData: observable.ref
		});

		each(webhookData.urls, this.addUrl);
		this.key = webhookData.key;
		this.conditionKey = webhookData.condition && webhookData.condition.key;
		this.conditionValue = webhookData.condition && webhookData.condition.value;
		this.uuid = webhookData.uuid;
		this.webhooksAppStore = webhooksAppStore;
		this._webhookFieldsTree = this.getWebhookTree(webhookData.model_config);
		this.isFlexible = webhookData.enableFlexibleWebhook ?? false;
		this.newStructure = webhookData.newStructure ?? false;
		this.webhookAuthenticationConfigurationId = webhookData.webhook_authentication_configuration_id;
		this.headersData = webhookData.headers ? { ...webhookData.headers } : undefined;
		this.baseBodyData = webhookData.baseBody ? { ...webhookData.baseBody } : {};
	}

	static cloneWebhook(webhook: Webhook): Webhook {
		return new Webhook(webhook.toJson(), webhook.webhooksAppStore);
	}

	setConditionKey = (conditionKey: string) => {
		this.conditionKey = conditionKey;
	};

	setConditionValue = (conditionValue: string) => {
		this.conditionValue = conditionValue;
	};

	setWebhookFieldsTree = (webhookFieldsTree: WebhookFieldsTree) => {
		this._webhookFieldsTree = webhookFieldsTree;
	};

	setWebhookAuthenticationConfigurationId = (id: number) => {
		this.webhookAuthenticationConfigurationId = id;
	};

	setWebhookAuthenticationConfiguration = (
		webhookAuthenticationConfiguration: WebhookAuthenticationConfiguration
	) => {
		this.webhookAuthenticationConfiguration = webhookAuthenticationConfiguration;
	};

	doesSupportFlexibleWebhook(): boolean {
		const webhookType = this.webhooksAppStore?.applicationConfig?.[this.key];
		return Boolean(webhookType?.baseModel);
	}

	isOnlyFlexible(): boolean {
		const webhookType = this.webhooksAppStore?.applicationConfig?.[this.key];
		return Boolean(webhookType?.onlyFlexible);
	}

	getWebhookTree(webhookFieldsConfiguration?: WebhookAMC): WebhookFieldsTree {
		if (this.doesSupportFlexibleWebhook()) {
			const webhookType = this.webhooksAppStore?.applicationConfig?.[this.key];
			return formatTreeFromServer(webhookType, this.webhooksAppStore.webhookModels, webhookFieldsConfiguration);
		}
		return { tree: null, nodeValues: null, rootId: null };
	}

	setKey = (key: string) => {
		this.key = key;
		this.resetWebhookFieldsTree();
		this.setIsFlexible(this.doesSupportFlexibleWebhook());
	};

	addUrl = (url = ''): void => {
		this.urlsData.set(uuidv4(), url);
	};

	deleteUrl = (uuid: string): void => {
		this.urlsData.delete(uuid);
	};

	updateUrl = (uuid: string, url: string): void => {
		this.urlsData.set(uuid, url);
	};

	setIsFlexible = (isFlexible: boolean) => {
		this.isFlexible = isFlexible;
		this.newStructure = isFlexible;
	};

	updateNodeValues = newNodeValues => {
		this.setWebhookFieldsTree({ ...this.webhookFieldsTree, nodeValues: newNodeValues });
	};

	resetWebhookFieldsTree = () => {
		this.setWebhookFieldsTree(this.getWebhookTree());
	};

	save = async () => {
		if (isNil(this.uuid)) {
			this.uuid = uuidv4();
		}

		this.applyUnassignedConditionIfNeeded();

		await this.webhooksAppStore.updateWebhook(this);
	};

	/**
	 * An absent condition matches EVERY event of its key. This screen cannot supply a real one (the
	 * filter is disabled here; analytics-app writes the scheduled job id when a schedule selects the
	 * destination), so fall back to a sentinel no job id can equal - inert until a schedule claims it.
	 *
	 * Written into state rather than only into `toJson()` so the table reflects what was persisted
	 * without a page reload. Never overwrites an existing condition.
	 *
	 * Does not affect the Test button: `simulate_webhook` copies the condition's own value into the
	 * body at `condition.key` and POSTs directly, without running the condition matcher, so a test
	 * always sends. The sentinel is not numeric, so the simulated body carries a null job id.
	 */
	applyUnassignedConditionIfNeeded = (): void => {
		if (this.conditionKey || this.key !== WebhookKey.AnalyticsScheduledReportReady) {
			return;
		}

		this.setConditionKey(ANALYTICS_WEBHOOK_CONDITION_KEY);
		this.setConditionValue(ANALYTICS_WEBHOOK_UNASSIGNED_CONDITION_VALUE);
	};

	delete = async () => {
		await this.webhooksAppStore.deleteWebhook(this.uuid);
	};

	test = async urlUuid => {
		const { dashboardSdk } = getRootEnv();
		const rawUrl = this.urlsData.get(urlUuid);
		await dashboardSdk.sdk.application.executeAction('simulate_webhook', {
			key: this.key,
			url: normalizeUrl(rawUrl),
			condition: this.buildCondition(),
			headers: this.headersData,
			model_config: this.isFlexible
				? formatTreeToServer(
						this.webhookFieldsTree.tree,
						this.webhookFieldsTree.rootId,
						this.webhookFieldsTree.nodeValues
				  )
				: null
		});
	};

	get selectedFields(): string[][] {
		return formatTreeToSelectedFields(this.webhookFieldsTree.tree, this.webhookFieldsTree.nodeValues);
	}

	toJson(): WebhookData {
		return {
			uuid: this.uuid,
			key: this.key,
			urls: Array.from(this.urlsData.values()).map(normalizeUrl),
			condition: this.buildCondition(),
			model_config: this.isFlexible
				? formatTreeToServer(
						this.webhookFieldsTree.tree,
						this.webhookFieldsTree.rootId,
						this.webhookFieldsTree.nodeValues
				  )
				: null,
			enableFlexibleWebhook: this.isFlexible,
			newStructure: this.newStructure,
			webhook_authentication_configuration_id: this.webhookAuthenticationConfigurationId,
			headers: this.headersData,
			baseBody: this.baseBodyData
		};
	}

	buildCondition(): Condition {
		return this.conditionKey ? { key: this.conditionKey, value: this.conditionValue } : undefined;
	}

	get urls(): [string, string][] {
		return Array.from(this.urlsData.entries());
	}

	get webhookFieldsTree(): WebhookFieldsTree {
		return this._webhookFieldsTree;
	}

	get isValid(): boolean {
		if (this.conditionKey && isEmpty(this.conditionValue)) {
			return false;
		}

		const isEveryUrlValid = every(this.urls, ([_, url]) => !isEmpty(url) && isValidPublicUrl(url));
		if (this.urls.length === 0 || !isEveryUrlValid) {
			return false;
		}

		if (isEmpty(this.key)) {
			return false;
		}

		return true;
	}

	get headers(): Record<string, string> {
		return this.headersData ?? {};
	}

	setHeaders = (headers: Record<string, string>) => {
		this.headersData = headers;
	};

	get baseBody(): Record<string, string> {
		return this.baseBodyData ?? {};
	}

	setBaseBody = (baseBody: Record<string, string>) => {
		this.baseBodyData = baseBody;
	};
}

export default Webhook;
